import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './lib/api';
import { SkeletonTable } from './components/Skeleton';
import ClientAutocomplete from './components/ClientAutocomplete';
import FournisseurAutocomplete from './components/FournisseurAutocomplete';
import { useTvaRates } from './hooks/useTvaRates';
import { searchPcg, type PcgAccount } from './lib/pcg2026';

interface Transaction {
  id: string;
  date: string;
  libelle: string;
  type: 'recette' | 'depense' | null;
  piece_ref: string | null;
  tva_taux: number | null;
  ttc_centimes: number | null;
  ht_centimes: number | null;
  compte_principal: string | null;
  client_id: string | null;
  client_nom: string | null;
  fournisseur_id: string | null;
  fournisseur_nom: string | null;
  categorie_id: string | null;
  categorie_nom: string | null;
  justificatif_id: string | null;
  justificatif_filename: string | null;
  justificatifs?: { id: string; filename: string }[];
  statut_validation: string | null;
  lignes?: Array<{
    compte_id: string;
    debit_centimes: number;
    credit_centimes: number;
  }>;
  validated_at: string | null;
}

interface Category {
  id: string;
  nom: string;
  couleur?: string;
}

interface TransactionListProps {
  entrepriseId: string;
}

function exportTransactionsCSV(transactions: Transaction[]) {
  const headers = ['Date', 'Type', 'Compte PCG', 'Libellé', 'TTC', 'HT', 'TVA %', 'Client', 'Fournisseur', 'Catégorie', 'Statut', 'Pièce'];
  const rows = transactions.map(tx => [
    new Date(tx.date).toLocaleDateString('fr-FR'),
    tx.type === 'recette' ? 'Recette' : 'Dépense',
    tx.compte_principal || '',
    `"${(tx.libelle || '').replace(/"/g, '""')}"`,
    ((tx.ttc_centimes || 0) / 100).toFixed(2),
    tx.ht_centimes != null ? (tx.ht_centimes / 100).toFixed(2) : '',
    tx.tva_taux != null ? `${tx.tva_taux}` : '',
    tx.client_nom || '',
    tx.fournisseur_nom || '',
    tx.categorie_nom || '',
    tx.statut_validation,
    tx.piece_ref || '',
  ]);

  const csv = [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `transactions_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

const formatCurrency = (centimes: number) => {
  const euros = centimes / 100;
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(euros);
};

interface MobileCardProps {
  tx: Transaction;
  categories: Category[];
  onEdit: () => void;
  onDelete: () => void;
  onValidate: () => void;
  onCategoryChange: (catId: string | null) => void;
}

function MobileTransactionCard({ tx, categories, onEdit, onDelete, onValidate, onCategoryChange }: MobileCardProps) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
      {/* Card header — always visible */}
      <div
        className="flex items-center justify-between px-4 py-3 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className={`flex-shrink-0 inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
            tx.type === 'recette' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
          }`}>
            {tx.type === 'recette' ? 'Recette' : 'Dépense'}
          </span>
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{tx.libelle}</p>
            <p className="text-xs text-gray-400">{new Date(tx.date).toLocaleDateString('fr-FR')} · {tx.compte_principal || '-'}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <span className="text-sm font-semibold text-gray-900">{formatCurrency(tx.ttc_centimes || 0)}</span>
          <span className="text-gray-400 text-xs">{expanded ? '▲' : '▼'}</span>
        </div>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="border-t border-gray-50 px-4 py-3 space-y-2 bg-gray-50">
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div><span className="text-gray-400">HT</span> <span className="text-gray-700">{tx.ht_centimes != null ? formatCurrency(tx.ht_centimes) : '-'}</span></div>
            <div><span className="text-gray-400">TVA</span> <span className="text-gray-700">{tx.tva_taux != null ? `${tx.tva_taux}%` : '-'}</span></div>
            {tx.client_nom && <div><span className="text-gray-400">Client</span> <span className="text-gray-700">{tx.client_nom}</span></div>}
            {tx.fournisseur_nom && <div><span className="text-gray-400">Fournisseur</span> <span className="text-gray-700">{tx.fournisseur_nom}</span></div>}
            {tx.piece_ref && <div><span className="text-gray-400">Pièce</span> <span className="text-gray-700">{tx.piece_ref}</span></div>}
            {tx.justificatif_filename && <div><span className="text-gray-400">Justificatif</span> <span className="text-gray-700">{tx.justificatif_filename}</span></div>}
          </div>
          <div className="flex items-center gap-3 pt-1">
            <select
              value={tx.categorie_id || ''}
              onChange={e => onCategoryChange(e.target.value || null)}
              className="flex-1 text-xs px-2 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-amber-400"
            >
              <option value="">(aucune catégorie)</option>
              {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.nom}</option>)}
            </select>
            {tx.statut_validation === 'validated' ? (
              <span className="text-xs text-green-600 font-medium" title={tx.validated_at ? `Validé le ${new Date(tx.validated_at).toLocaleDateString('fr-FR')}` : 'Validé'}>✓</span>
            ) : (
              <button onClick={onValidate} className="text-xs text-gray-400 hover:text-green-600 transition-colors font-medium" title="Valider">Valider</button>
            )}
            <button onClick={onEdit} className="p-1.5 text-gray-400 hover:text-blue-500 transition-colors" title="Modifier">✏️</button>
            <button onClick={onDelete} className="p-1.5 text-gray-400 hover:text-red-500 transition-colors" title="Supprimer">🗑️</button>
          </div>
        </div>
      )}
    </div>
  );
}

const TransactionList: React.FC<TransactionListProps> = ({ entrepriseId }) => {
  const queryClient = useQueryClient();
  const { rates: tvaRates } = useTvaRates();
  const [deleteConfirm, setDeleteConfirm] = useState<Transaction | null>(null);
  const [validateConfirm, setValidateConfirm] = useState<Transaction | null>(null);
  const [editTx, setEditTx] = useState<Transaction | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ libelle: '', date: '', piece_ref: '' });
  const [editType, setEditType] = useState<'recette' | 'depense' | null>(null);
  const [editTtcInput, setEditTtcInput] = useState('');
  const [editHtInput, setEditHtInput] = useState('');
  const [editTauxTva, setEditTauxTva] = useState<number>(20);
  const [editComptePrincipal, setEditComptePrincipal] = useState('');
  const [editCompteSearch, setEditCompteSearch] = useState('');
  const [editCompteOptions, setEditCompteOptions] = useState<PcgAccount[]>([]);
  const [editClientId, setEditClientId] = useState<string | null>(null);
  const [editFournisseurId, setEditFournisseurId] = useState<string | null>(null);
  const [editJustificatifs, setEditJustificatifs] = useState<{ id: string; filename: string; pendingRemove: boolean }[]>([]);
  const [editNewFiles, setEditNewFiles] = useState<{ file: File; localUrl: string | null; id: string }[]>([]);
  const [editUploading, setEditUploading] = useState(false);
  const editFileInputRef = useRef<HTMLInputElement>(null);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [batchValidateConfirm, setBatchValidateConfirm] = useState(false);
  const [batchDeleteConfirm, setBatchDeleteConfirm] = useState(false);

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === sortedTransactions.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(sortedTransactions.map(t => t.id)));
    }
  };

  const parseAmount = (s: string): number | '' => {
    const normalized = s.replace(',', '.');
    const n = parseFloat(normalized);
    return isNaN(n) ? '' : n;
  };

  const handleEditTtcChange = (raw: string) => {
    setEditTtcInput(raw);
    const val = parseAmount(raw);
    if (val === '' || val === 0) { setEditHtInput(''); return; }
    const ht = Math.round((val / (1 + editTauxTva / 100)) * 100) / 100;
    setEditHtInput(String(ht));
  };

  const handleEditHtChange = (raw: string) => {
    setEditHtInput(raw);
    const val = parseAmount(raw);
    if (val === '' || val === 0) { setEditTtcInput(''); return; }
    const ttc = Math.round(val * (1 + editTauxTva / 100) * 100) / 100;
    setEditTtcInput(String(ttc));
  };

  const handleEditTvaChange = (taux: number) => {
    setEditTauxTva(taux);
    const ttc = parseAmount(editTtcInput);
    if (ttc !== '') {
      const ht = Math.round((ttc / (1 + taux / 100)) * 100) / 100;
      setEditHtInput(String(ht));
    }
  };

  useEffect(() => {
    if (editCompteSearch.length < 2) { setEditCompteOptions([]); return; }
    setEditCompteOptions(searchPcg(editCompteSearch).slice(0, 30));
  }, [editCompteSearch]);

  const [filters, setFilters] = useState({
    type: '',
    compte_principal: '',
    date_from: '',
    date_to: '',
    libelle_contains: '',
    skip: 0,
    take: 20,
  });

  type SortKey = 'date' | 'type' | 'compte_principal' | 'libelle' | 'ttc_centimes' | 'ht_centimes' | 'tva_taux' | 'client_nom' | 'fournisseur_nom' | 'categorie_nom' | 'statut_validation';
  const [sortBy, setSortBy] = useState<SortKey>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const FILTERS_KEY = 'bilanexpress_tx_filters';

  // Restore filters from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(FILTERS_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.filters) setFilters(parsed.filters);
        if (parsed.sortBy) setSortBy(parsed.sortBy);
        if (parsed.sortOrder) setSortOrder(parsed.sortOrder);
      }
    } catch { /* ignore corrupt data */ }
  }, []);

  // Save filters to localStorage on change
  useEffect(() => {
    localStorage.setItem(FILTERS_KEY, JSON.stringify({ filters, sortBy, sortOrder }));
  }, [filters, sortBy, sortOrder]);

  const filterParams: Record<string, string> = {};
  if (filters.type) filterParams.type = filters.type;
  if (filters.compte_principal) filterParams.compte_principal = filters.compte_principal;
  if (filters.date_from) filterParams.date_from = filters.date_from;
  if (filters.date_to) filterParams.date_to = filters.date_to;
  if (filters.libelle_contains) filterParams.libelle_contains = filters.libelle_contains;
  filterParams.skip = filters.skip.toString();
  filterParams.take = filters.take.toString();
  if (sortBy) filterParams.sortBy = sortBy;
  if (sortOrder) filterParams.sortOrder = sortOrder;

  const { data: categoriesData } = useQuery({
    queryKey: ['categories'],
    queryFn: () => api.categories.list(),
  });
  const categories: Category[] = categoriesData || [];

  const { data: txData, isLoading: loading, error: txError } = useQuery({
    queryKey: ['transactions', entrepriseId, filterParams],
    queryFn: () => api.transactions.list(entrepriseId, filterParams),
  });
  const transactions: Transaction[] = txData?.items || [];
  const total: number = txData?.total || 0;
  const error = txError ? (txError as Error).message : null;

  const categoryMutation = useMutation({
    mutationFn: ({ txId, categorieId }: { txId: string; categorieId: string | null }) =>
      api.transactions.patch(txId, { categorie_id: categorieId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions', entrepriseId] });
    },
  });

  const updateCategory = (txId: string, categorieId: string | null) => {
    categoryMutation.mutate({ txId, categorieId });
  };

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.transactions.delete(id, entrepriseId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions', entrepriseId] });
      setDeleteConfirm(null);
      setActionError(null);
    },
    onError: (e: any) => setActionError(e.message || 'Erreur lors de la suppression'),
  });

  const validateMutation = useMutation({
    mutationFn: (txId: string) =>
      api.transactions.patch(txId, {
        entreprise_id: entrepriseId,
        statut_validation: 'validated',
        validated_at: new Date().toISOString(),
      } as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions', entrepriseId] });
    },
    onError: (e: any) => setActionError(e.message || 'Erreur lors de la validation'),
  });

  const editMutation = useMutation({
    mutationFn: (data: any) => api.transactions.patch(editTx!.id, { ...data, entreprise_id: entrepriseId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions', entrepriseId] });
      setEditTx(null);
      setEditCompteOptions([]);
      setActionError(null);
    },
    onError: (e: any) => setActionError(e.message || 'Erreur lors de la modification'),
  });

  const openEdit = (tx: Transaction) => {
    setEditForm({
      libelle: tx.libelle,
      date: new Date(tx.date).toISOString().slice(0, 10),
      piece_ref: tx.piece_ref || '',
    });
    setEditTtcInput(String((tx.ttc_centimes || 0) / 100));
    const taux = tx.tva_taux ?? 20;
    setEditTauxTva(taux);
    const ttcEuros = (tx.ttc_centimes || 0) / 100;
    const ht = Math.round((ttcEuros / (1 + taux / 100)) * 100) / 100;
    setEditHtInput(String(ht));
    setEditComptePrincipal(tx.compte_principal || '');
    setEditCompteSearch(tx.compte_principal || '');
    setEditType(tx.type);
    setEditClientId(tx.client_id);
    setEditFournisseurId(tx.fournisseur_id);
    setEditJustificatifs((tx.justificatifs ?? []).map(j => ({ ...j, pendingRemove: false })));
    setEditNewFiles([]);
    setEditTx(tx);
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: name === 'skip' || name === 'take' ? Number(value) : value,
    }));
  };

  const applyFilters = (e: React.FormEvent) => {
    e.preventDefault();
    setFilters(prev => ({ ...prev, skip: 0 }));
  };

  const clearFilters = () => {
    setFilters({
      type: '',
      compte_principal: '',
      date_from: '',
      date_to: '',
      libelle_contains: '',
      skip: 0,
      take: 20,
    });
    setSortBy('date');
    setSortOrder('desc');
    localStorage.removeItem(FILTERS_KEY);
  };

  const handleSort = (column: SortKey) => {
    if (sortBy === column) {
      setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (column: SortKey) => {
    if (sortBy !== column) return <span className="ml-1 text-gray-300">↕</span>;
    return <span className="ml-1 text-blue-500">{sortOrder === 'asc' ? '↑' : '↓'}</span>;
  };

  const sortedTransactions = useMemo(() => {
    const str = (v: string | null | undefined) => v ?? '';
    const num = (v: number | null | undefined) => v ?? 0;

    const primaryCompare = (a: Transaction, b: Transaction): number => {
      switch (sortBy) {
        case 'date': return a.date.localeCompare(b.date);
        case 'type': return str(a.type).localeCompare(str(b.type));
        case 'compte_principal': return str(a.compte_principal).localeCompare(str(b.compte_principal), 'fr');
        case 'libelle': return a.libelle.localeCompare(b.libelle, 'fr');
        case 'ttc_centimes': return num(a.ttc_centimes) - num(b.ttc_centimes);
        case 'ht_centimes': return num(a.ht_centimes) - num(b.ht_centimes);
        case 'tva_taux': return num(a.tva_taux) - num(b.tva_taux);
        case 'client_nom': return str(a.client_nom).localeCompare(str(b.client_nom), 'fr');
        case 'fournisseur_nom': return str(a.fournisseur_nom).localeCompare(str(b.fournisseur_nom), 'fr');
        case 'categorie_nom': return str(a.categorie_nom).localeCompare(str(b.categorie_nom), 'fr');
        case 'statut_validation': return str(a.statut_validation).localeCompare(str(b.statut_validation));
        default: return 0;
      }
    };

    const secondaryCompare = (a: Transaction, b: Transaction): number => {
      // Date primary: secondary by compte; all others: secondary by date asc
      if (sortBy === 'date') return str(a.compte_principal).localeCompare(str(b.compte_principal), 'fr');
      return a.date.localeCompare(b.date);
    };

    return [...transactions].sort((a, b) => {
      const primary = primaryCompare(a, b);
      if (primary !== 0) return sortOrder === 'asc' ? primary : -primary;
      return secondaryCompare(a, b);
    });
  }, [transactions, sortBy, sortOrder]);

  const inputClass = "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1";

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Liste des transactions</h2>

      <form onSubmit={applyFilters} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className={labelClass}>Type</label>
            <select name="type" value={filters.type} onChange={handleFilterChange} className={inputClass}>
              <option value="">Tous</option>
              <option value="recette">Recette</option>
              <option value="depense">Dépense</option>
            </select>
          </div>
          <div>
            <label className={labelClass}>Compte PCG</label>
            <input type="text" name="compte_principal" value={filters.compte_principal} onChange={handleFilterChange} placeholder="ex: 512, 706" className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Libellé</label>
            <input type="text" name="libelle_contains" value={filters.libelle_contains} onChange={handleFilterChange} placeholder="Mot-clé" className={inputClass} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className={labelClass}>Date du</label>
            <input type="date" name="date_from" value={filters.date_from} onChange={handleFilterChange} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Date au</label>
            <input type="date" name="date_to" value={filters.date_to} onChange={handleFilterChange} className={inputClass} />
          </div>
        </div>
        <div className="flex gap-3">
          <button type="submit" disabled={loading} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">Filtrer</button>
          <button type="button" onClick={clearFilters} className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors">Réinitialiser</button>
          <button
            type="button"
            onClick={() => exportTransactionsCSV(sortedTransactions)}
            className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
          >
            Exporter CSV
          </button>
        </div>
      </form>

      {error && <div className="text-red-700 bg-red-50 border border-red-200 px-4 py-3 rounded-lg text-sm mb-4">{error}</div>}
      {loading ? (
        <SkeletonTable rows={8} />
      ) : (
        <>
          <p className="text-sm text-gray-500 mb-3">Total: {total} transaction{total !== 1 ? 's' : ''}</p>
          {selectedIds.size > 0 && (
            <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 mb-3">
              <span className="text-sm font-medium text-blue-700">
                {selectedIds.size} sélectionnée(s)
              </span>
              <button
                onClick={() => setBatchValidateConfirm(true)}
                className="px-3 py-1.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700"
              >
                Valider ({sortedTransactions.filter(t => selectedIds.has(t.id) && t.statut_validation === 'draft').length})
              </button>
              <button
                onClick={() => setBatchDeleteConfirm(true)}
                className="px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700"
              >
                Supprimer ({selectedIds.size})
              </button>
            </div>
          )}
          {/* Mobile card view */}
          <div className="md:hidden space-y-3 mb-4">
            {transactions.map(tx => (
              <MobileTransactionCard
                key={tx.id}
                tx={tx}
                categories={categories}
                onEdit={() => openEdit(tx)}
                onDelete={() => setDeleteConfirm(tx)}
                onValidate={() => setValidateConfirm(tx)}
                onCategoryChange={(catId) => updateCategory(tx.id, catId)}
              />
            ))}
          </div>

          {/* Desktop table */}
          <div className="hidden md:block bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-3 py-3 w-8">
                    <input
                      type="checkbox"
                      checked={selectedIds.size === sortedTransactions.length && sortedTransactions.length > 0}
                      onChange={toggleSelectAll}
                      className="rounded border-gray-300"
                    />
                  </th>
                  {([
                    ['date', 'Date', 'text-left'],
                    ['type', 'Type', 'text-left'],
                    ['compte_principal', 'Compte PCG', 'text-left'],
                    ['libelle', 'Libellé', 'text-left'],
                    ['ttc_centimes', 'TTC', 'text-right'],
                    ['ht_centimes', 'HT', 'text-right'],
                    ['tva_taux', 'TVA', 'text-left'],
                    ['client_nom', 'Client', 'text-left'],
                    ['fournisseur_nom', 'Fournisseur', 'text-left'],
                    ['categorie_nom', 'Catégorie', 'text-left'],
                  ] as [SortKey, string, string][]).map(([key, label, align]) => (
                    <th
                      key={key}
                      className={`${align} px-3 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide cursor-pointer hover:bg-gray-100 select-none`}
                      onClick={() => handleSort(key)}
                    >
                      {label}{getSortIcon(key)}
                    </th>
                  ))}
                  <th className="text-left px-3 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Pièce</th>
                  <th className="text-left px-3 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Justificatif</th>
                  <th
                    className="text-left px-3 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('statut_validation')}
                  >
                    Statut{getSortIcon('statut_validation')}
                  </th>
                  <th className="px-3 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {sortedTransactions.map(tx => (
                  <tr key={tx.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-3 py-3 border-b border-gray-100">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(tx.id)}
                        onChange={() => toggleSelect(tx.id)}
                        className="rounded border-gray-300"
                      />
                    </td>
                    <td className="px-3 py-3 text-gray-700 border-b border-gray-100 whitespace-nowrap">{new Date(tx.date).toLocaleDateString('fr-FR')}</td>
                    <td className="px-3 py-3 border-b border-gray-100">
                      <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${tx.type === 'recette' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {tx.type === 'recette' ? 'Recette' : 'Dépense'}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-gray-700 border-b border-gray-100 text-sm">{tx.compte_principal || '-'}</td>
                    <td className="px-3 py-3 text-gray-700 border-b border-gray-100 max-w-[200px] truncate">{tx.libelle}</td>
                    <td className="px-3 py-3 text-gray-700 border-b border-gray-100 text-right font-medium whitespace-nowrap">{formatCurrency(tx.ttc_centimes || 0)}</td>
                    <td className="px-3 py-3 text-gray-700 border-b border-gray-100 text-right whitespace-nowrap">{tx.ht_centimes != null ? formatCurrency(tx.ht_centimes) : '-'}</td>
                    <td className="px-3 py-3 text-gray-500 border-b border-gray-100 text-xs">{tx.tva_taux !== null ? `${tx.tva_taux}%` : '-'}</td>
                    <td className="px-3 py-3 text-gray-500 border-b border-gray-100 text-xs">{tx.client_nom || '-'}</td>
                    <td className="px-3 py-3 text-gray-500 border-b border-gray-100 text-xs">{tx.fournisseur_nom || '-'}</td>
                    <td className="px-3 py-3 border-b border-gray-100">
                      <select
                        value={tx.categorie_id || ''}
                        onChange={e => updateCategory(tx.id, e.target.value || null)}
                        className="text-xs px-2 py-1 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                      >
                        <option value="">(aucune)</option>
                        {categories.map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.nom}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-3 text-gray-500 border-b border-gray-100 text-xs">{tx.piece_ref || '-'}</td>
                    <td className="px-3 py-3 text-gray-500 border-b border-gray-100 text-xs">{tx.justificatif_filename || '-'}</td>
                    <td className="px-3 py-3 border-b border-gray-100">
                      {tx.statut_validation === 'validated' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700" title={tx.validated_at ? `Validé le ${new Date(tx.validated_at).toLocaleDateString('fr-FR')}` : 'Validé'}>
                          ✓ Validé
                        </span>
                      ) : (
                        <button
                          onClick={() => setValidateConfirm(tx)}
                          className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-500 hover:bg-green-100 hover:text-green-700 transition-colors"
                          title="Valider l'écriture"
                        >
                          Valider
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3 border-b border-gray-100">
                      <div className="flex items-center gap-2 justify-end">
                        {tx.statut_validation !== 'validated' && (
                          <button
                            onClick={() => openEdit(tx)}
                            className="text-gray-400 hover:text-blue-500 transition-colors"
                            title="Modifier"
                          >
                            ✏️
                          </button>
                        )}
                        <button
                          onClick={() => setDeleteConfirm(tx)}
                          className="text-gray-400 hover:text-red-500 transition-colors"
                          title="Supprimer"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {(() => {
            const totalPages = Math.max(1, Math.ceil(total / filters.take));
            const currentPage = Math.floor(filters.skip / filters.take) + 1;
            const goToPage = (page: number) =>
              setFilters(prev => ({ ...prev, skip: (page - 1) * prev.take }));

            const buildPages = (): (number | '…')[] => {
              if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
              const pages: (number | '…')[] = [1];
              if (currentPage > 3) pages.push('…');
              for (let p = Math.max(2, currentPage - 1); p <= Math.min(totalPages - 1, currentPage + 1); p++) pages.push(p);
              if (currentPage < totalPages - 2) pages.push('…');
              pages.push(totalPages);
              return pages;
            };

            return (
              <div className="flex items-center justify-center gap-1 flex-wrap">
                <button
                  disabled={currentPage === 1}
                  onClick={() => goToPage(currentPage - 1)}
                  className="px-3 py-1.5 text-sm rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  ‹
                </button>
                {buildPages().map((p, i) =>
                  p === '…' ? (
                    <span key={`ellipsis-${i}`} className="px-2 text-gray-400 text-sm">…</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => goToPage(p as number)}
                      className={`w-9 h-9 text-sm rounded-lg font-medium transition-colors ${
                        p === currentPage
                          ? 'bg-amber-500 text-white shadow-sm'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {p}
                    </button>
                  )
                )}
                <button
                  disabled={currentPage === totalPages}
                  onClick={() => goToPage(currentPage + 1)}
                  className="px-3 py-1.5 text-sm rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  ›
                </button>
              </div>
            );
          })()}
        </>
      )}
      {/* Delete confirm modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Confirmer la suppression</h3>
            <p className="text-sm text-gray-600 mb-1">Supprimer cette écriture ?</p>
            <p className="text-sm font-medium text-gray-900 mb-4">"{deleteConfirm.libelle}" — {formatCurrency(deleteConfirm.ttc_centimes || 0)}</p>
            {actionError && <p className="text-red-600 text-xs mb-3">{actionError}</p>}
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">Annuler</button>
              <button
                onClick={() => deleteMutation.mutate(deleteConfirm.id)}
                disabled={deleteMutation.isPending}
                className="flex-1 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 disabled:opacity-50 transition-colors"
              >
                {deleteMutation.isPending ? 'Suppression...' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Validate confirm modal */}
      {validateConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Confirmer la validation</h3>
            <p className="text-sm text-gray-600 mb-1">Valider cette écriture ?</p>
            <p className="text-sm font-medium text-gray-900 mb-4">"{validateConfirm.libelle}" — {formatCurrency(validateConfirm.ttc_centimes || 0)}</p>
            {actionError && <p className="text-red-600 text-xs mb-3">{actionError}</p>}
            <div className="flex gap-3">
              <button onClick={() => { setValidateConfirm(null); setActionError(null); }} className="flex-1 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">Annuler</button>
              <button
                onClick={() => { validateMutation.mutate(validateConfirm.id); setValidateConfirm(null); }}
                disabled={validateMutation.isPending}
                className="flex-1 py-2 text-sm font-medium text-white bg-green-500 rounded-lg hover:bg-green-600 disabled:opacity-50 transition-colors"
              >
                {validateMutation.isPending ? 'Validation...' : 'Valider'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Batch validate confirm modal */}
      {batchValidateConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Confirmer la validation</h3>
            <p className="text-sm text-gray-600 mb-4">
              Valider {sortedTransactions.filter(t => selectedIds.has(t.id) && t.statut_validation === 'draft').length} écriture(s) sélectionnée(s) ?
            </p>
            <div className="flex gap-3">
              <button onClick={() => setBatchValidateConfirm(false)} className="flex-1 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">Annuler</button>
              <button
                onClick={async () => {
                  const draftIds = sortedTransactions
                    .filter(t => selectedIds.has(t.id) && t.statut_validation === 'draft')
                    .map(t => t.id);
                  if (draftIds.length > 0) {
                    await api.transactions.batchValidate(draftIds, entrepriseId);
                    queryClient.invalidateQueries({ queryKey: ['transactions', entrepriseId] });
                  }
                  setSelectedIds(new Set());
                  setBatchValidateConfirm(false);
                }}
                className="flex-1 py-2 text-sm font-medium text-white bg-green-500 rounded-lg hover:bg-green-600 transition-colors"
              >
                Valider
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Batch delete confirm modal */}
      {batchDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Confirmer la suppression</h3>
            <p className="text-sm text-gray-600 mb-4">
              Supprimer {selectedIds.size} écriture(s) sélectionnée(s) ? Cette action est irréversible.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setBatchDeleteConfirm(false)} className="flex-1 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">Annuler</button>
              <button
                onClick={async () => {
                  await Promise.all([...selectedIds].map(id => api.transactions.delete(id, entrepriseId)));
                  queryClient.invalidateQueries({ queryKey: ['transactions', entrepriseId] });
                  setSelectedIds(new Set());
                  setBatchDeleteConfirm(false);
                }}
                className="flex-1 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editTx && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Modifier l'écriture</h3>
            <div className="space-y-3">
              {/* Type toggle */}
              <div className="flex border-b border-gray-100 mb-1">
                <button
                  type="button"
                  onClick={() => { setEditType('recette'); setEditClientId(null); setEditFournisseurId(null); }}
                  className={`flex-1 py-2 text-sm text-center transition-colors ${
                    editType === 'recette' ? 'border-b-2 border-emerald-500 text-emerald-700 font-semibold bg-emerald-50' : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  RECETTE
                </button>
                <button
                  type="button"
                  onClick={() => { setEditType('depense'); setEditClientId(null); setEditFournisseurId(null); }}
                  className={`flex-1 py-2 text-sm text-center transition-colors ${
                    editType === 'depense' ? 'border-b-2 border-red-500 text-red-700 font-semibold bg-red-50' : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  DÉPENSE
                </button>
              </div>
              {/* Date */}
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Date</label>
                <input
                  type="date"
                  value={editForm.date}
                  onChange={(e) => setEditForm((f) => ({ ...f, date: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
              </div>
              {/* Libellé */}
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Libellé</label>
                <input
                  value={editForm.libelle}
                  onChange={(e) => setEditForm((f) => ({ ...f, libelle: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
              </div>
              {/* TTC | HT | TVA — 4/4/2 cols */}
              <div className="grid grid-cols-10 gap-3">
                <div className="col-span-4">
                  <label className="block text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">TTC</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={editTtcInput}
                    onChange={(e) => handleEditTtcChange(e.target.value)}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                  />
                </div>
                <div className="col-span-4">
                  <label className="block text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">HT</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={editHtInput}
                    onChange={(e) => handleEditHtChange(e.target.value)}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">TVA</label>
                  <select
                    value={editTauxTva}
                    onChange={(e) => handleEditTvaChange(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                  >
                    {tvaRates.map((r) => (
                      <option key={r} value={r}>{r}%</option>
                    ))}
                  </select>
                </div>
              </div>
              {/* Compte PCG */}
              <div className="relative">
                <label className="block text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Compte PCG</label>
                <input
                  type="text"
                  value={editCompteSearch}
                  onChange={(e) => setEditCompteSearch(e.target.value)}
                  placeholder="Rechercher un compte"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
                {editCompteOptions.length > 0 && (
                  <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                    {editCompteOptions.map((acc) => (
                      <li
                        key={acc.numero}
                        onClick={() => {
                          setEditComptePrincipal(acc.numero);
                          setEditCompteSearch(`${acc.numero} — ${acc.libelle}`);
                          setEditCompteOptions([]);
                        }}
                        className="px-4 py-2 text-sm hover:bg-amber-50 cursor-pointer border-b border-gray-50 last:border-0"
                      >
                        <strong>{acc.numero}</strong> — {acc.libelle}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {/* Client / Fournisseur */}
              {editType === 'recette' ? (
                <div>
                  <label className="block text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Client <span className="text-gray-300">(optionnel)</span></label>
                  <ClientAutocomplete
                    entrepriseId={entrepriseId}
                    value={editClientId}
                    onChange={setEditClientId}
                    displayName={editTx?.client_nom}
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Fournisseur <span className="text-gray-300">(optionnel)</span></label>
                  <FournisseurAutocomplete
                    entrepriseId={entrepriseId}
                    value={editFournisseurId}
                    onChange={setEditFournisseurId}
                    displayName={editTx?.fournisseur_nom}
                  />
                </div>
              )}
              {/* Réf. pièce */}
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Réf. pièce</label>
                <input
                  value={editForm.piece_ref}
                  onChange={(e) => setEditForm((f) => ({ ...f, piece_ref: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
              </div>
              {/* Justificatif (1 fichier max) */}
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-2">
                  Justificatif <span className="text-gray-300">(optionnel)</span>
                </label>
                <input
                  ref={editFileInputRef}
                  type="file"
                  className="hidden"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    const activeCount = editJustificatifs.filter(j => !j.pendingRemove).length + editNewFiles.length;
                    if (activeCount >= 1) return;
                    if (f.size > 10 * 1024 * 1024) {
                      setActionError('Taille maximum : 10 Mo');
                      return;
                    }
                    setEditNewFiles([{ file: f, localUrl: null, id: Math.random().toString(36).slice(2) }]);
                    if (editFileInputRef.current) editFileInputRef.current.value = '';
                  }}
                />
                <div className="space-y-2">
                  {/* Existing justificatif (not pending remove) */}
                  {editJustificatifs.filter(j => !j.pendingRemove).map(j => (
                    <div
                      key={j.id}
                      className="flex items-center gap-2 rounded-xl border border-gray-100 bg-gray-50 px-3 py-2 text-sm"
                    >
                      <span className="text-lg">{j.filename.toLowerCase().endsWith('.pdf') ? '📄' : '🖼️'}</span>
                      <span className="truncate flex-1 text-gray-700">{j.filename}</span>
                      <button
                        type="button"
                        onClick={() => setEditJustificatifs(prev =>
                          prev.map(x => x.id === j.id ? { ...x, pendingRemove: true } : x)
                        )}
                        className="text-gray-400 hover:text-red-500 text-sm font-bold"
                        title="Supprimer"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  {/* New file pending upload */}
                  {editNewFiles.map(nf => (
                    <div key={nf.id} className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm">
                      <span className="text-lg">📄</span>
                      <span className="truncate flex-1 text-gray-700">{nf.file.name}</span>
                      <button
                        type="button"
                        onClick={() => setEditNewFiles([])}
                        className="text-gray-400 hover:text-red-500 text-sm font-bold"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
                {/* Add file button — only when no active file */}
                {(editJustificatifs.filter(j => !j.pendingRemove).length + editNewFiles.length) === 0 && (
                  <button
                    type="button"
                    onClick={() => editFileInputRef.current?.click()}
                    className="mt-2 w-full border-2 border-dashed border-gray-200 rounded-xl py-3 text-sm text-gray-500 hover:border-amber-300 hover:bg-amber-50 transition-colors"
                  >
                    + Ajouter un fichier
                  </button>
                )}
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              {actionError && <p className="text-red-600 text-xs mb-2">{actionError}</p>}
              <button
                onClick={() => {
                  setEditJustificatifs([]);
                  setEditNewFiles([]);
                  setEditTx(null);
                  setEditCompteOptions([]);
                  setActionError(null);
                }}
                className="flex-1 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={async () => {
                  try {
                    // Upload new files
                    setEditUploading(true);
                    const uploadedIds: string[] = [];
                    for (const nf of editNewFiles) {
                      const uploaded = await api.justificatifs.upload(nf.file, entrepriseId);
                      uploadedIds.push(uploaded.id);
                    }
                    setEditUploading(false);

                    // Delete removed justificatifs from storage
                    const removedIds = editJustificatifs.filter(j => j.pendingRemove).map(j => j.id);
                    await Promise.all(removedIds.map(id => api.justificatifs.delete(id, entrepriseId).catch(() => {})));

                    // Build final IDs: keep non-removed existing + newly uploaded
                    const keptIds = editJustificatifs.filter(j => !j.pendingRemove).map(j => j.id);
                    const finalIds = [...keptIds, ...uploadedIds];


                    const htVal = parseAmount(editHtInput);
                    editMutation.mutate({
                      ...editForm,
                      type: editType,
                      ht_centimes: htVal !== '' ? Math.round(htVal * 100) : undefined,
                      taux_tva: editTauxTva,
                      compte_principal: editComptePrincipal || undefined,
                      client_id: editType === 'recette' ? editClientId : null,
                      fournisseur_id: editType === 'depense' ? editFournisseurId : null,
                      justificatif_ids: finalIds,
                    });
                  } catch (e: any) {
                    setEditUploading(false);
                    setActionError(e.message || 'Erreur upload');
                  }
                }}
                disabled={editMutation.isPending || editUploading}
                className="flex-1 py-2 text-sm font-medium text-white bg-amber-500 rounded-lg hover:bg-amber-600 disabled:opacity-50 transition-colors"
              >
                {editMutation.isPending || editUploading ? 'Enregistrement...' : 'Enregistrer »'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionList;
